import { ChangeEvent, FC } from 'react';

import { isValid, parse, isEighteenOrOlder } from '@eneco/common/date';
import { capitalizeFirstLetter } from '@eneco/common/string';
import { emailValidation } from '@eneco/common/validation/emailRegex';
import RichText from '@eneco/components/RichText/RichText';
import { FlowSpinner } from '@eneco/flows/components';
import { Fields } from '@eneco/flows/types';
import { I18nProvider, useFormatter, useTranslation } from '@eneco/i18n';
import {
  Box,
  Checkbox,
  Grid,
  Heading,
  InputDate,
  InputEmail,
  InputTelephone,
  InputText,
  RadioButton,
  RadioGroup,
  Stack,
  Text,
} from '@eneco/sparky';
import { Controller } from 'react-hook-form';
import * as yup from 'yup';

import { CustomerDetailsHighlights } from './CustomerDetailsHighlights';
import { StepShell } from '../../components/StepShell/StepShell';
import { formatInitials, addLeadingZero } from '../../helpers';
import { useFlowStep } from '../../hooks';
import { NAME_PATTERN, OPTIONAL_NAME_PATTERN, DUTCH_PHONE_PATTERN } from '../validationPatterns';

interface CustomerDetailsStepProps {
  /**
   * Renders the reassurance side panel next to the form in a two-column layout
   * (exp-n-477 variant C). Defaults to the centered single-column layout
   * (variant B).
   */
  withHighlights?: boolean;
}

interface FormFields {
  salutation: string;
  initials: string;
  firstName: string;
  surnamePreposition: string;
  surname: string;
  birthdate: string;
  day: string | number;
  month: string | number;
  year: string | number;
  phoneNumber: string;
  emailAddress?: string;
  C1?: boolean;
  C2?: boolean;
  C3?: boolean;
}

const SALUTATION_OPTIONS = ['dhr', 'mevr', 'neutral'] as const;

/**
 * A/B variation (exp-n-477) that merges the PersonalDetailsStep and
 * ContactDetailsStep into a single page. This combined route has no Sitecore
 * rendering, so all copy and validation messages come from the bundled i18n
 * dictionary (`content/<locale>.json`) instead of `usePlaceholderContent`.
 */
const CustomerDetailsStepForm: FC<CustomerDetailsStepProps> = ({ withHighlights }) => {
  const { t } = useTranslation();
  const { date } = useFormatter();

  const buildSchema = () =>
    yup.object({
      salutation: yup.string().required(t('personalDetails.salutation.requiredMessage')),
      initials: yup
        .string()
        .required(t('personalDetails.initials.requiredMessage'))
        .test('initials max', t('personalDetails.initials.validationMaxValueMessage'), (value = '') => {
          return value.toUpperCase().replace(/[^A-Z]/g, '').length <= 6;
        }),
      firstName: yup
        .string()
        .required(t('personalDetails.firstName.requiredMessage'))
        .matches(NAME_PATTERN, t('personalDetails.firstName.validationMessage')),
      surnamePreposition: yup
        .string()
        .matches(OPTIONAL_NAME_PATTERN, t('personalDetails.surnamePreposition.validationMessage')),
      surname: yup
        .string()
        .required(t('personalDetails.surname.requiredMessage'))
        .matches(NAME_PATTERN, t('personalDetails.surname.validationMessage')),
      day: yup
        .number()
        .typeError(t('personalDetails.day.validationMessage'))
        .required(t('personalDetails.day.requiredMessage'))
        .min(1, t('personalDetails.day.validationMessage'))
        .max(31, t('personalDetails.day.validationMessage')),
      month: yup
        .number()
        .typeError(t('personalDetails.month.validationMessage'))
        .required(t('personalDetails.month.requiredMessage'))
        .min(1, t('personalDetails.month.validationMessage'))
        .max(12, t('personalDetails.month.validationMessage')),
      year: yup
        .number()
        .typeError(t('personalDetails.year.validationMessage'))
        .required(t('personalDetails.year.requiredMessage'))
        .min(1900, t('personalDetails.year.validationMessage')),
      birthdate: yup
        .object({})
        .test('valid date', t('personalDetails.birthdate.validationMessage'), (_, context) => {
          const {
            parent: { day, month, year },
          } = context;

          if (!day || !month || !year) {
            return false;
          }

          return isValid(year, month, day);
        })
        .test('minimum age', t('personalDetails.birthdate.validationMinValueMessage'), (_, context) => {
          const {
            parent: { day, month, year },
          } = context;
          return isEighteenOrOlder(year, month, day);
        }),
      phoneNumber: yup
        .string()
        .required(t('contactDetails.phoneNumber.requiredMessage'))
        .matches(DUTCH_PHONE_PATTERN, t('contactDetails.phoneNumber.validationMessage')),
      emailAddress: emailValidation({
        emailMessage: t('contactDetails.emailAddress.validationMessage'),
        requiredMessage: t('contactDetails.emailAddress.requiredMessage'),
      }),
      C1: yup.boolean(),
      C2: yup.boolean(),
      C3: yup.boolean(),
    });

  const {
    context: { isPersonalDetailsEditProhibited },
    form: {
      handleSubmit,
      control,
      setValue,
      register,
      formState: { errors, isSubmitSuccessful },
    },
  } = useFlowStep<FormFields>({
    buildSchema,
    mode: 'onTouched',
    defaultValues: context => ({
      salutation: context.salutation,
      initials: context.initials,
      firstName: context.firstName,
      surnamePreposition: context.surnamePreposition,
      surname: context.surname,
      day: context.birthdate && addLeadingZero(parse(context.birthdate).getDate()),
      month: context.birthdate && addLeadingZero(parse(context.birthdate).getMonth() + 1),
      year: context.birthdate && parse(context.birthdate).getFullYear(),
      phoneNumber: context.phoneNumber,
      emailAddress: context.emailAddress,
      C1: context.C1 ?? true,
      C2: context.C2 ?? false,
      C3: context.C3,
    }),
    onSubmitSuccess: formData => ({
      salutation: formData.salutation,
      initials: formData.initials,
      firstName: formData.firstName,
      surnamePreposition: formData.surnamePreposition || undefined,
      surname: formData.surname,
      birthdate: date.short(new Date(Number(formData.year), Number(formData.month) - 1, Number(formData.day))),
      phoneNumber: formData.phoneNumber,
      emailAddress: formData.emailAddress,
      C1: formData.C1,
      C2: formData.C2,
      C3: formData.C3,
    }),
  });

  const textualData: Fields = {
    content: {
      title: { value: t('content.title') },
      text: { value: t('content.text') },
      notification: { value: { content: '', title: '' } },
    },
  };

  const emailAddressText = t('contactDetails.emailAddressText');

  return (
    <StepShell
      handleSubmit={handleSubmit}
      textualData={textualData}
      isLoading={isSubmitSuccessful}
      nextButtonLabel={t('content.nextStepText')}
      variant={withHighlights ? 'C' : 'A'}
      aside={withHighlights ? <CustomerDetailsHighlights /> : undefined}>
      <Controller
        control={control}
        name="salutation"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            label={t('personalDetails.salutation.label')}
            name={name}
            direction="row"
            value={value}
            onValueChange={onChange}
            error={errors.salutation?.message}>
            {SALUTATION_OPTIONS.map(option => (
              <RadioButton value={option} key={option}>
                {t(`personalDetails.salutation.options.${option}`)}
              </RadioButton>
            ))}
          </RadioGroup>
        )}
      />

      <InputText
        {...register('firstName', {
          onBlur: (event: ChangeEvent<HTMLInputElement>) => {
            setValue('firstName', capitalizeFirstLetter(event?.target?.value?.trim()));
          },
        })}
        hint={t('personalDetails.firstName.hint')}
        label={t('personalDetails.firstName.label')}
        error={errors.firstName?.message}
      />

      <Grid columns="2" columnGap="3">
        <InputText
          {...register('initials', {
            onBlur: (event: ChangeEvent<HTMLInputElement>) => {
              setValue('initials', formatInitials(event.target.value));
            },
          })}
          hint={t('personalDetails.initials.hint')}
          label={t('personalDetails.initials.label')}
          error={errors.initials?.message}
        />
        <InputText
          {...register('surnamePreposition')}
          hint={t('personalDetails.surnamePreposition.hint')}
          label={t('personalDetails.surnamePreposition.label')}
          error={errors.surnamePreposition?.message}
        />
      </Grid>

      <InputText
        {...register('surname', {
          onBlur: (event: ChangeEvent<HTMLInputElement>) => {
            setValue('surname', capitalizeFirstLetter(event?.target?.value?.trim()));
          },
        })}
        hint={t('personalDetails.surname.hint')}
        label={t('personalDetails.surname.label')}
        error={errors.surname?.message}
      />

      <InputDate
        label={t('personalDetails.birthdate.label')}
        name="birthdate"
        hint={t('personalDetails.birthdate.hint')}
        error={errors.birthdate?.message ?? errors.year?.message ?? errors.month?.message ?? errors.day?.message}>
        <InputDate.Day
          {...register('day', {
            onBlur: (event: ChangeEvent<HTMLInputElement>) => {
              if (event.target.value) setValue('day', addLeadingZero(event.target.value));
            },
          })}
          label={t('personalDetails.day.label')}
          hasError={!!errors.day?.message}
        />
        <InputDate.Day
          {...register('month', {
            onBlur: (event: ChangeEvent<HTMLInputElement>) => {
              if (event.target.value) setValue('month', addLeadingZero(event.target.value));
            },
          })}
          label={t('personalDetails.month.label')}
          hasError={!!errors.month?.message}
        />
        <InputDate.Day
          {...register('year')}

          label={t('personalDetails.year.label')}
          hasError={!!errors.year?.message}
        />
      </InputDate>

      <InputTelephone
        {...register('phoneNumber')}
        hint={t('contactDetails.phoneNumber.hint')}
        label={t('contactDetails.phoneNumber.label')}
        autoComplete="tel"
        error={errors.phoneNumber?.message}
      />

      <Stack.Item>
        <InputEmail
          {...register('emailAddress')}
          hint={t('contactDetails.emailAddress.hint')}
          label={t('contactDetails.emailAddress.label')}
          autoComplete="email"
          error={errors.emailAddress?.message}
        />

        {emailAddressText && (
          <Box paddingTop="4">
            <Text size="BodyS" color="textLowEmphasis">
              <RichText html={emailAddressText} />
            </Text>
          </Box>
        )}
      </Stack.Item>

      {!isPersonalDetailsEditProhibited && (
        <>
          <Stack.Item>
            <Stack direction="column" gap="4">
              <Heading as="h2" size="S">
                {t('optIn.title')}
              </Heading>
              <RichText html={t('optIn.content')} />
            </Stack>
          </Stack.Item>

          <Stack.Item>
            <Stack direction="column" gap="2">
              <Checkbox label={t('optIn.checkBoxProductOffersFirstParty')} {...register('C1')} />
              <Checkbox label={t('optIn.checkBoxContactByTelephone')} {...register('C2')} />
              <Checkbox label={t('optIn.checkBoxProductOffersThirdParty')} {...register('C3')} />
            </Stack>
          </Stack.Item>
        </>
      )}
    </StepShell>
  );
};

/**
 * Gates the form behind the async dictionary load. The yup schema is built once
 * when the form mounts, so we must wait until the translations are available
 * before rendering it — otherwise validation messages would be empty.
 */
const CustomerDetailsStepGate: FC<CustomerDetailsStepProps> = ({ withHighlights }) => {
  const { isLoading } = useTranslation();

  if (isLoading) {
    return <FlowSpinner />;
  }

  return <CustomerDetailsStepForm withHighlights={withHighlights} />;
};

const CustomerDetailsStepBase: FC<CustomerDetailsStepProps> = ({ withHighlights }) => (
  <I18nProvider dictionary={locale => import(`./content/${locale}.json`)}>
    <CustomerDetailsStepGate withHighlights={withHighlights} />
  </I18nProvider>
);

/** exp-n-477 variant B: consolidated details on a single, centered page. */
export const CustomerDetailsStep: FC = () => <CustomerDetailsStepBase />;

/** exp-n-477 variant C: consolidated details with a reassurance side panel. */
export const CustomerDetailsStepWithHighlights: FC = () => <CustomerDetailsStepBase withHighlights />;
